import React, { useEffect, useRef, useState } from 'react';
import CountdownOverlay from './CountdownOverlay';
import { useSmartScroll } from '../../hooks/useSmartScroll';
import '../../App.css';

/**
 * Preview UI showing camera/microphone before recording starts
 * Shows live preview with a "Start Recording" button
 * Handles scroll and countdown before starting recording (unless disableAutoScroll is true)
 */
function PreviewSection({ type, onStartRecording, onCountdownChange, promptText, disableAutoScroll = false, autoStart = false }) {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const promptBoxRef = useRef(null);
  const [previewStream, setPreviewStream] = useState(null);
  const [error, setError] = useState(null);
  const [showCountdown, setShowCountdown] = useState(false);
  const streamHandedOffRef = useRef(false);

  const { scrollToTarget } = useSmartScroll({ targetRef: promptBoxRef, fallbackRef: containerRef });

  useEffect(() => {
    let stream = null;
    let unmounted = false;

    // Get preview stream
    const initPreview = async () => {
      try {
        const constraints = type === 'video'
          ? { video: true, audio: true }
          : { audio: true };

        stream = await navigator.mediaDevices.getUserMedia(constraints);

        // If component unmounted while waiting for getUserMedia, stop immediately
        if (unmounted) {
          stream.getTracks().forEach(track => track.stop());
          return;
        }

        setPreviewStream(stream);

        if (type === 'video' && videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      } catch (err) {
        if (!unmounted) {
          console.error('Error accessing media devices:', err);
          setError(err.message);
        }
      }
    };

    initPreview();

    // Cleanup on unmount - only stop stream if it wasn't handed off to recording
    return () => {
      unmounted = true;
      if (stream && !streamHandedOffRef.current) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [type]);

  // Auto-start countdown when autoStart is true and stream is ready
  useEffect(() => {
    if (autoStart && previewStream && !showCountdown) {
      handleStart();
    }
  }, [autoStart, previewStream]); // eslint-disable-line react-hooks/exhaustive-deps

  // Notify parent when countdown state changes (for blur effect)
  useEffect(() => {
    if (onCountdownChange) {
      onCountdownChange(showCountdown);
    }
  }, [showCountdown, onCountdownChange]);

  const handleStart = () => {
    // Show prompt box immediately
    setShowCountdown(true);
    // Wait for DOM update, then scroll to position prompt box at 10px from top
    // Skip if parent handles its own scrolling (e.g., Studio)
    if (!disableAutoScroll) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => scrollToTarget());
      });
    }
  };

  const handleCountdownComplete = () => {
    // Pass the existing stream to recording to avoid camera restart delay
    streamHandedOffRef.current = true;
    onStartRecording(previewStream);
  };

  if (error) {
    return (
      <div className="preview-error">
        <p>❌ Could not access {type === 'video' ? 'camera/microphone' : 'microphone'}</p>
        <p style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
          {error}
        </p>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      {promptText && showCountdown && (
        <div
          ref={promptBoxRef}
          className="recording-prompt-box"
        >
          {promptText}
        </div>
      )}
      <div ref={containerRef} className="preview-container" style={{ position: 'relative' }}>
        {type === 'video' ? (
          <div style={{ position: 'relative' }}>
            <video
              ref={videoRef}
              autoPlay
              muted
              style={{
                width: '100%',
                maxHeight: '400px',
                background: '#000',
                borderRadius: '0.5rem',
                marginBottom: '1rem'
              }}
            />
            {showCountdown && <CountdownOverlay onComplete={handleCountdownComplete} />}
          </div>
        ) : (
          <div style={{ position: 'relative' }}>
            <div style={{
              padding: '2rem',
              textAlign: 'center',
              background: 'var(--color-surface)',
              borderRadius: '0.5rem',
              marginBottom: '1rem',
              minHeight: '200px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>🎤</div>
              <p>Microphone ready</p>
            </div>
            {showCountdown && <CountdownOverlay onComplete={handleCountdownComplete} />}
          </div>
        )}

        {!showCountdown && !autoStart && (
          <div style={{ textAlign: 'center' }}>
            <button
              className="btn-primary"
              onClick={handleStart}
            >
              🔴 Start Recording
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default PreviewSection;
